<template>
  <div class="google-trend-chart">
    <div ref="chart" class="google-trend-chart__canvas"></div>
    <div v-if="isEmpty" class="google-trend-chart__empty">暂无趋势数据</div>
  </div>
</template>

<script>
import * as echarts from "echarts/core";
import {
  GridComponent,
  LegendComponent,
  TooltipComponent,
} from "echarts/components";
import { LineChart } from "echarts/charts";
import { CanvasRenderer } from "echarts/renderers";

echarts.use([
  GridComponent,
  LegendComponent,
  TooltipComponent,
  LineChart,
  CanvasRenderer,
]);

export default {
  name: "GoogleTrendChart",
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    height: {
      type: Number,
      default: 260,
    },
  },
  data() {
    return {
      chart: null,
    };
  },
  computed: {
    isEmpty() {
      return !this.rows.some(
        (item) =>
          Number(item.sessions || 0) ||
          Number(item.active_users || 0) ||
          Number(item.search_clicks || 0) ||
          Number(item.search_impressions || 0)
      );
    },
  },
  watch: {
    rows: {
      deep: true,
      handler() {
        this.renderChart();
      },
    },
  },
  mounted() {
    this.$refs.chart.style.height = `${this.height}px`;
    this.chart = echarts.init(this.$refs.chart);
    this.renderChart();
    window.addEventListener("resize", this.resizeChart);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resizeChart);
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  },
  methods: {
    resizeChart() {
      if (this.chart) {
        this.chart.resize();
      }
    },
    renderChart() {
      if (!this.chart) return;
      const labels = this.rows.map((item) => item.month);
      this.chart.setOption({
        color: ["#1677ff", "#13c2c2", "#7c5cff", "#f59a23"],
        tooltip: {
          trigger: "axis",
          confine: true,
        },
        legend: {
          top: 0,
          left: 0,
          itemWidth: 18,
          itemHeight: 8,
          textStyle: {
            color: "#607089",
            fontSize: 12,
          },
        },
        grid: {
          top: 42,
          right: 22,
          bottom: 28,
          left: 44,
        },
        xAxis: {
          type: "category",
          boundaryGap: false,
          data: labels,
          axisLine: { lineStyle: { color: "#dfe6f0" } },
          axisTick: { show: false },
          axisLabel: { color: "#607089" },
        },
        yAxis: {
          type: "value",
          minInterval: 1,
          splitLine: { lineStyle: { color: "#edf1f7" } },
          axisLabel: { color: "#607089" },
        },
        series: [
          this.lineSeries("GA4访问数", "sessions", true),
          this.lineSeries("GA4访客数", "active_users", false),
          this.lineSeries("GSC点击", "search_clicks", false),
          this.lineSeries("GSC曝光", "search_impressions", false),
        ],
      });
      this.$nextTick(this.resizeChart);
    },
    lineSeries(name, key, area) {
      return {
        name,
        type: "line",
        smooth: true,
        symbol: "circle",
        symbolSize: 6,
        data: this.rows.map((item) => Number(item[key] || 0)),
        areaStyle: area ? { opacity: 0.08 } : undefined,
        lineStyle: { width: 2 },
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.google-trend-chart {
  position: relative;
  min-height: 220px;

  &__canvas {
    width: 100%;
  }

  &__empty {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 12px;
    text-align: center;
    color: #909399;
    font-size: 13px;
    pointer-events: none;
  }
}
</style>
